"use client";
import React, {
  forwardRef,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useTranslation } from "react-i18next";

import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "~/components/ui/command";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "~/components/ui/popover";

import { cn } from "~/lib/utils";

import { CheckIcon, ChevronDown } from "lucide-react";
import { CircleFlag } from "react-circle-flags";

import { countries } from "country-data-list";

interface PhonePrefixSelectorProps {
  value?: string;
  onChange?: (prefix: string) => void;
  defaultValue?: string;
  disabled?: boolean;
  className?: string;
  searchLabel?: string;
}

interface CountryOption {
  alpha2: string;
  name: string;
  callingCode: string;
  emoji: string;
}

const PhonePrefixSelectorComponent = (
  {
    value,
    onChange,
    defaultValue = "+39",
    disabled = false,
    className,
    searchLabel = "Cerca paese o prefisso...",
    ...props
  }: PhonePrefixSelectorProps,
  ref: React.ForwardedRef<HTMLButtonElement>
) => {
  const { i18n, t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [selectedPrefix, setSelectedPrefix] = useState<string>(
    value || defaultValue
  );
  const [searchValue, setSearchValue] = useState("");
  const commandListRef = useRef<HTMLDivElement>(null);

  const baseCountries = useMemo(() => {
    return countries.all
      .filter(
        (c: any) =>
          c.countryCallingCodes &&
          c.countryCallingCodes.length > 0 &&
          c.emoji &&
          c.status !== "deleted"
      )
      .map((c: any) => ({
        alpha2: c.alpha2,
        name: c.name,
        callingCode: c.countryCallingCodes[0],
        emoji: c.emoji,
      }));
  }, []);

  const countryNameFormatter = useMemo(() => {
    const locale = i18n.language === "it" ? "it" : "en";
    return new Intl.DisplayNames([locale], { type: "region" });
  }, [i18n.language]);

  const getCountryName = useCallback(
    (country: CountryOption) => {
      try {
        const translatedName = countryNameFormatter.of(country.alpha2);
        return translatedName || country.name;
      } catch {
        return country.name;
      }
    },
    [countryNameFormatter]
  );

  const availableCountries = useMemo(() => {
    return baseCountries
      .map((country: CountryOption) => ({
        ...country,
        name: getCountryName(country),
      }))
      .sort((a: CountryOption, b: CountryOption) =>
        a.name.localeCompare(b.name)
      );
  }, [baseCountries, getCountryName]);

  const selectedCountry = useMemo(() => {
    return availableCountries.find(
      (c: CountryOption) => c.callingCode === selectedPrefix
    );
  }, [availableCountries, selectedPrefix]);

  useEffect(() => {
    if (value !== undefined) {
      setSelectedPrefix(value);
    }
  }, [value]);

  useEffect(() => {
    if (defaultValue && !value) {
      setSelectedPrefix(defaultValue);
    }
  }, [defaultValue, value]);

  useEffect(() => {
    if (open && commandListRef.current) {
      commandListRef.current.scrollTop = 0;
      setSearchValue("");
    }
  }, [open]);

  const handleSelect = useCallback(
    (country: CountryOption) => {
      setSelectedPrefix(country.callingCode);
      onChange?.(country.callingCode);
      setOpen(false);
    },
    [onChange]
  );

  const triggerClasses = cn(
    "flex h-9 items-center justify-between whitespace-nowrap border border-input bg-transparent px-3 py-2 text-sm shadow-sm ring-offset-background placeholder:text-muted-foreground focus:outline-none focus:ring-1 focus:ring-ring disabled:cursor-not-allowed disabled:opacity-50 w-auto max-w-fit rounded-none",
    className
  );

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger
        ref={ref}
        className={triggerClasses}
        disabled={disabled}
        {...props}
      >
        {selectedCountry ? (
          <div className="flex items-center gap-2 pr-2">
            <CircleFlag
              countryCode={selectedCountry.alpha2.toLowerCase()}
              height={16}
              width={16}
            />
            <span className="text-sm">{selectedPrefix}</span>
          </div>
        ) : (
          <span className="text-sm">{selectedPrefix}</span>
        )}
        <ChevronDown className="h-4 w-4 opacity-50 ml-2" />
      </PopoverTrigger>
      <PopoverContent
        collisionPadding={10}
        side="bottom"
        className="w-[300px] p-0 z-[60]"
        align="start"
      >
        <Command className="w-full max-h-[200px] sm:max-h-[270px] overflow-hidden">
          <CommandList
            ref={commandListRef}
            className="overflow-y-scroll"
            onWheel={(e) => {
              e.stopPropagation();
              const element = e.currentTarget;
              element.scrollTop += e.deltaY;
            }}
          >
            <div className="sticky top-0 z-10 bg-popover">
              <CommandInput
                placeholder={searchLabel}
                value={searchValue}
                onValueChange={(value) => {
                  setSearchValue(value);
                  if (commandListRef.current) {
                    commandListRef.current.scrollTop = 0;
                  }
                }}
              />
            </div>
            <CommandEmpty>{t("Nessun paese trovato")}</CommandEmpty>
            <CommandGroup>
              {availableCountries.map((country: CountryOption, index: number) => (
                <CommandItem
                  className="flex items-center w-full gap-2"
                  key={index}
                  value={`${country.name} ${country.callingCode} ${country.alpha2}`}
                  onSelect={() => handleSelect(country)}
                >
                  <div className="flex flex-grow w-0 space-x-2 overflow-hidden">
                    <div className="inline-flex items-center justify-center w-5 h-5 shrink-0 overflow-hidden rounded-full">
                      <CircleFlag
                        countryCode={country.alpha2.toLowerCase()}
                        height={20}
                      />
                    </div>
                    <span className="overflow-hidden text-ellipsis whitespace-nowrap">
                      {country.name}
                    </span>
                  </div>
                  <span className="text-sm text-muted-foreground ml-auto mr-2">
                    {country.callingCode}
                  </span>
                  <CheckIcon
                    className={cn(
                      "h-4 w-4 shrink-0",
                      selectedPrefix === country.callingCode
                        ? "opacity-100"
                        : "opacity-0"
                    )}
                  />
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
};

PhonePrefixSelectorComponent.displayName = "PhonePrefixSelectorComponent";

export const PhonePrefixSelector = forwardRef(PhonePrefixSelectorComponent);

